<template>
	<div
		v-if="colors && colors.length > 0"
		class="ecommerce-product-color-picker"
		ref="colorPicker"
	>
		<div v-if="colors.length > 1">
			<TnParagraph
				class="color-text"
				:size="content?.headingSize"
				responsive
				bold
			>
				{{ colorPickerText || "Velg farge" }}
			</TnParagraph>
			<ColorPicker
				:colors="colors"
				size="md"
				:model-value="colorFromPath"
				@update:modelValue="colorChanged"
				:disable-colors-not-in-stock="content.disableColorsNotInStock"
				unique
			/>
		</div>
		<div v-else-if="colors && colors.length === 1">
			<TnParagraph
				:size="content?.headingSize"
				class="color-text"
				responsive
				bold
				>{{ colorPickerText || "Velg farge" }}</TnParagraph
			>
			<div class="single-color">
				<Color
					:color="colors[0].colorCode"
					size="md"
					active
					:label="colors[0].label || colors[0].colorCode"
					:showLabel="true"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import GizmoComponentMixin from "~/mixins/platform/GizmoComponentMixin.js";
import ECommerceProductMixin from "~/components/ecommerce/ECommerceProductMixin/ECommerceProductMixin.js";
import capitalize from "~/helpers/formatting/capitalize";
import safeEcommerceUrl from "~/helpers/ecommerce/safeEcommerceUrl";
import { mapState } from "vuex";
import { mapActions } from "pinia";
import { usePageStore } from "~/pinia/platform/page.ts";

export default defineNuxtComponent({
	name: "ECommerceProductColorPicker",
	mixins: [GizmoComponentMixin, ECommerceProductMixin],

	data() {
		return {
			product: null,
			color: "",
		};
	},

	props: {
		handleColorChangeManually: {
			type: Boolean,
		},
	},

	computed: {
		...mapState("ecommerce", ["categoriesThatRequireStock"]),
		colors() {
			const mappedVariants = this.sortedVariants.map((variant) => ({
				colorCode: variant.colorCode?.toUpperCase(),
				label: capitalize(variant.color),
				inStock:
					this.eCommerceProduct?.variants?.some(
						(v) => v.color === variant.color && (v.inventory?.length || (v.inventory && v.inventory[0]?.amount)),
					) || !this.categoriesThatRequireStock.includes(this.eCommerceProduct?.category),
				link: variant.href,
			}));

			return mappedVariants.filter((mV) => mV.colorCode && mV.label && mV.inStock);
		},
		colorPickerText() {
			return this.content.heading;
		},

		sortedVariants() {
			if (this.eCommerceProduct?.variants?.length > 0) {
				const variantsCopy = JSON.parse(JSON.stringify(this.eCommerceProduct?.variants))?.map((x) =>
					!x.colorCode ? { ...x, colorCode: "#FFF" } : x,
				);

				variantsCopy.sort((a, b) => {
					if (a.color > b.color) return 1;
					else if (a.color < b.color) return -1;
					return 0;
				});

				const uniqueVariants = [];

				variantsCopy.forEach((vc) => {
					if (uniqueVariants.find((uv) => uv.colorCode === vc.colorCode))
						// This color has already been resolved
						return;
					const matchingVariants = variantsCopy.filter((v) => v.colorCode === vc.colorCode);
					const memoryMatch = matchingVariants.find(
						(mv) =>
							(mv.memory === this.eCommerceProduct?.memory && mv.condition === this.eCommerceProduct?.condition) ||
							mv.memory === this.eCommerceProduct?.memory,
					);
					if (memoryMatch) uniqueVariants.push(memoryMatch);
					else uniqueVariants.push(matchingVariants[0]);
				});

				return uniqueVariants.map((variant) => {
					return {
						...variant,
						href: safeEcommerceUrl(
							this.eCommerceProduct?.category,
							this.eCommerceProduct?.brand,
							variant.title,
							variant?.condition,
						),
					};
				});
			} else if (this.eCommerceProduct?.colorCode) {
				return [this.eCommerceProduct];
			} else {
				return [];
			}
		},
		colorFromPath: {
			get() {
				return this.eCommerceProduct?.colorCode?.toUpperCase() || "#FFF";
			},
			set(color) {
				return color;
			},
		},
	},

	methods: {
		...mapActions(usePageStore, ["loadPage"]),
		async colorChanged(color) {
			this.loading = true;

			const resolvedVariant = this.sortedVariants.find((variant) => variant.colorCode?.toUpperCase() === color);

			if (!this.handleColorChangeManually) {
				this.$store.commit("ecommerce/setProduct", {
					...this.$store.state.ecommerce.product,
					...resolvedVariant,
					loading: true,
				}); // Fake update to have instant update

				useAnalytics().resetProducts();

				const variantUrl = safeEcommerceUrl(
					this.eCommerceProduct?.category,
					this.eCommerceProduct?.brand,
					resolvedVariant.title,
					this.eCommerceProduct?.condition,
				);

				if (import.meta.client) {
					history.replaceState(null, "", variantUrl + window.location.hash);
				}

				await this.loadPage(variantUrl);

				useAnalytics().page.productLoaded({
					products: useAnalytics().getProducts(),
				});

				if (import.meta.client && !this.eCommerceProduct.disableClickAndCollect) {
					this.locationPending = true;
					await useGeolocationStore().requestBestAvailablePosition(true);
					const position = useGeolocationStore().position;
					this.locationPending = false;
					this.$store.dispatch("ecommerce/getStoresWithProduct", {
						gtin: this.$store.state.ecommerce.product.gtin,
						location: position,
					});
				}
			} else {
				this.$emit("change", resolvedVariant);
			}
		},
	},
});
</script>

<style lang="scss" scoped>
.color-text {
	margin-bottom: $spacing-m;
	color: $color-neutrals-900-shade;
}
</style>
